import type PlanningSetting from './planning-setting';
import type { IPlanningSettingData, IUpdatePlanningSettingsCmd } from './types/planning-settings';

// This data is used to set the default values for the planning settings
// when creating a new planning setting.
// We should always try to keep the available properties in sync with the backend.
export const DEFAULT_PLANNING_SETTING_DATA: IPlanningSettingData = {
  aduHorizonOffset: undefined,
  aduMin: undefined,
  aduOverride: undefined,
  aduProfileId: undefined,
  aduType: 'ROLLING',
  bufferProfileId: undefined,
  coverageHorizon: undefined,
  dafGreenMonthly: {},
  dafGreenUnit: 'PERCENT',
  dafGreenWeekly: {},
  dafLtMonthly: {},
  dafLtUnit: 'PERCENT',
  dafLtWeekly: {},
  dafMonthly: {},
  dafProfileId: undefined,
  dafRedBaseMonthly: {},
  dafRedBaseUnit: 'PERCENT',
  dafRedBaseWeekly: {},
  dafRedSafetyMonthly: {},
  dafRedSafetyUnit: 'PERCENT',
  dafRedSafetyWeekly: {},
  dafUnit: 'PERCENT',
  dafWeekly: {},
  dafYellowMonthly: {},
  dafYellowUnit: 'PERCENT',
  dafYellowWeekly: {},
  demandHorizon: undefined,
  discontinuedDemand: false,
  fixedGreen: undefined,
  fixedGreenUnit: undefined,
  fixedRedBase: undefined,
  fixedRedBaseUnit: undefined,
  fixedRedSafety: undefined,
  fixedRedSafetyUnit: undefined,
  fixedYellow: undefined,
  fixedYellowUnit: undefined,
  isProtected: false,
  leadTimeFactor: undefined,
  methodology: undefined,
  orderCycle: undefined,
  orderCycleStrategy: undefined,
  orderSpikeHorizon: undefined,
  orderSpikeHorizonUnit: 'DAYS',
  orderSpikeLtHorizon: undefined,
  orderSpikeLtHorizonUnit: 'DAYS',
  orderSpikeLtThreshold: undefined,
  orderSpikeThreshold: undefined,
  reorderStrategy: undefined,
  safetyDays: undefined,
  safetyStock: undefined,
  safetyStockFormula: undefined,
  targetServiceRate: undefined,
  varFactor: undefined,
  aduHorizon: undefined
};
export default class UpdatePlanningSettingsCmd implements IUpdatePlanningSettingsCmd {
  skuId?: string;
  skuIds?: string[];
  settings: IPlanningSettingData = DEFAULT_PLANNING_SETTING_DATA;

  constructor(ps?: PlanningSetting) {
    if (!ps) {
      return this;
    }

    this.skuId = ps.skuId;
    this.settings = {
      ...ps.settings,
      aduHorizon: ps.aduProfile ? ps.aduProfile.aduHorizon : ps.settings.aduHorizon,
      aduHorizonOffset: ps.aduProfile
        ? ps.aduProfile.aduHorizonOffset
        : ps.settings.aduHorizonOffset,
      aduMin: ps.aduProfile ? ps.aduProfile.aduMin : ps.settings.aduMin,
      aduType: ps.aduProfile?.aduType ?? ps.settings.aduType,
      aduOverride: ps.aduProfile ? ps.aduProfile.aduOverride : ps.settings.aduOverride,
      varFactor: ps.bufferProfile?.varFactor ?? ps.settings.varFactor,
      leadTimeFactor: ps.bufferProfile?.leadTimeFactor ?? ps.settings.leadTimeFactor,
      fixedGreen: ps.bufferProfile?.fixedGreen ?? ps.settings.fixedGreen,
      fixedYellow: ps.bufferProfile?.fixedYellow ?? ps.settings.fixedYellow,
      fixedRedBase: ps.bufferProfile?.fixedRedBase ?? ps.settings.fixedRedBase,
      fixedRedSafety: ps.bufferProfile?.fixedRedSafety ?? ps.settings.fixedRedSafety,
      fixedGreenUnit: ps.bufferProfile?.fixedGreenUnit ?? ps.settings.fixedGreenUnit,
      fixedYellowUnit: ps.bufferProfile?.fixedYellowUnit ?? ps.settings.fixedYellowUnit,
      fixedRedBaseUnit: ps.bufferProfile?.fixedRedBaseUnit ?? ps.settings.fixedRedBaseUnit,
      fixedRedSafetyUnit: ps.bufferProfile?.fixedRedSafetyUnit ?? ps.settings.fixedRedSafetyUnit,
      discontinuedDemand: ps.actualDiscontinuedDemand,
      dafUnit: ps.dafProfile?.dafUnit ?? ps.settings.dafUnit,
      dafRedBaseUnit: ps.dafProfile?.dafRedBaseUnit ?? ps.settings.dafRedBaseUnit,
      dafRedSafetyUnit: ps.dafProfile?.dafRedSafetyUnit ?? ps.settings.dafRedSafetyUnit,
      dafYellowUnit: ps.dafProfile?.dafYellowUnit ?? ps.settings.dafYellowUnit,
      dafGreenUnit: ps.dafProfile?.dafGreenUnit ?? ps.settings.dafGreenUnit,
      dafLtUnit: ps.dafProfile?.dafLtUnit ?? ps.settings.dafLtUnit,
      dafGreenMonthly: ps.dafProfile?.dafGreenMonthly ?? ps.settings.dafGreenMonthly,
      dafGreenWeekly: ps.dafProfile?.dafGreenWeekly ?? ps.settings.dafGreenWeekly,
      dafLtMonthly: ps.dafProfile?.dafLtMonthly ?? ps.settings.dafLtMonthly,
      dafLtWeekly: ps.dafProfile?.dafLtWeekly ?? ps.settings.dafLtWeekly,
      dafMonthly: ps.dafProfile?.dafMonthly ?? ps.settings.dafMonthly,
      dafRedBaseMonthly: ps.dafProfile?.dafRedBaseMonthly ?? ps.settings.dafRedBaseMonthly,
      dafRedBaseWeekly: ps.dafProfile?.dafRedBaseWeekly ?? ps.settings.dafRedBaseWeekly,
      dafRedSafetyMonthly: ps.dafProfile?.dafRedSafetyMonthly ?? ps.settings.dafRedSafetyMonthly,
      dafRedSafetyWeekly: ps.dafProfile?.dafRedSafetyWeekly ?? ps.settings.dafRedSafetyWeekly,
      dafWeekly: ps.dafProfile?.dafWeekly ?? ps.settings.dafWeekly,
      dafYellowMonthly: ps.dafProfile?.dafYellowMonthly ?? ps.settings.dafYellowMonthly,
      dafYellowWeekly: ps.dafProfile?.dafYellowWeekly ?? ps.settings.dafYellowWeekly
    };
  }

  toNative(): object {
    return JSON.parse(JSON.stringify(this));
  }
}
